import './ProductTile.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';

import { PriceBlock } from './PriceBlock';

export interface ProductTileProps extends b2x.ProductTileProps {
  light?: boolean;
}

export const ProductTile = ({ light, product, ...otherProps }: ProductTileProps) => {
  const inStock = product.skus?.find((sku) => sku.state === 'AVAILABLE');

  return (
    <b2x.ProductTile
      className={classnames(
        'card',
        { 'in-stock': inStock },
        { 'out-of-stock': !inStock },
        { 'product-tile-light': light }
      )}
      product={product}
      {...otherProps}
      enableExtraInfo
    />
  );
};

export type ProductTileBasicInfoProps = b2x.ProductTileBasicInfoProps;

export const ProductTileBasicInfo = (props: ProductTileBasicInfoProps) => (
  <b2x.ProductTileBasicInfo {...props} customComponent={CustomProductTileBasicInfo} />
);

const CustomProductTileBasicInfo = ({
  alternativeImageFirst,
  colIndex,
  imageFormat,
  product,
  productsPerRow,
  sku,
}: b2x.ProductTileBasicInfoProps) => {
  const productImage = product.image;
  const productAlternativeImage = sku?.image
    ? sku.image
    : product.alternativeImages?.at(0)
    ? product.alternativeImages.at(0)
    : undefined;

  const tileImage = alternativeImageFirst && productAlternativeImage ? productAlternativeImage : productImage;
  const tileAlternativeImage =
    alternativeImageFirst && productAlternativeImage ? productImage : productAlternativeImage;

  return (
    <div className="product-tile-basic-info">
      <div className="position-relative">
        <b2x.router.Link to={product.url}>
          <div className="product-media-container">
            <b2x.EqualHeightElement
              colIndex={colIndex}
              name={productsPerRow ? `product-media-container-${productsPerRow}` : 'product-media-container'}
            >
              <div className="image">
                <b2x.ImageSwitcher
                  alt={product.name}
                  aspectRatio={b2x.appConfig.productImageAspectRatio}
                  fluid
                  format={imageFormat}
                  src={tileImage?.src}
                />
              </div>
              {tileImage && tileAlternativeImage && (
                <div className="alternative-image">
                  <b2x.ImageSwitcher
                    alt={product.name}
                    aspectRatio={b2x.appConfig.productImageAspectRatio}
                    fluid
                    format={imageFormat}
                    src={tileAlternativeImage.src}
                  />
                </div>
              )}
            </b2x.EqualHeightElement>
          </div>
        </b2x.router.Link>
      </div>
    </div>
  );
};

export type ProductTileExtraInfoProps = b2x.ProductTileExtraInfoProps;

export const ProductTileExtraInfo = (props: ProductTileExtraInfoProps) => (
  <b2x.ProductTileExtraInfo customComponent={CustomProductTileExtraInfo} {...props} />
);

const CustomProductTileExtraInfo = ({
  colIndex,
  fieldsHelper,
  priceHelper,
  product,
  productsPerRow,
  selectedSku,
}: ProductTileExtraInfoProps) => {
  const currentBreakpoint = b2x.useBreakpoint();

  return (
    <div className="product-tile-extra-info py-3">
      <b2x.router.Link className="text-decoration-none" to={product.url}>
        <b2x.EqualHeightElement colIndex={colIndex} name={`product-name-container-${productsPerRow}`}>
          <h3
            className={classnames(
              'fw-bold mb-1 mb-md-2',
              { h5: b2x.untilBreakpoint('sm', currentBreakpoint) },
              { h2: !b2x.untilBreakpoint('sm', currentBreakpoint) }
            )}
          >
            {product.name?.toUpperCase()}
          </h3>
        </b2x.EqualHeightElement>
        <b2x.EqualHeightElement colIndex={colIndex} name={`product-secondary-info-container-${productsPerRow}`}>
          {/*           {selectedSku?.name && (
            <p className={classnames('mb-1 mb-md-2', { 'h5 mb-2': !b2x.untilBreakpoint('sm', currentBreakpoint) })}>
              {selectedSku.name}
            </p>
          )}
 */}
          {product.descriptionShort && (
            <p className={classnames('mb-1 mb-md-2', { 'h5 mb-2': !b2x.untilBreakpoint('sm', currentBreakpoint) })}>
              {product.descriptionShort}
            </p>
          )}
        </b2x.EqualHeightElement>
        {priceHelper && (
          <b2x.Div className="product-price-container">
            <PriceBlock hiddenDiscountPercentage priceHelper={priceHelper} />
            {b2x.appConfig.enableBestPrice && <b2x.BestPrice priceHelper={priceHelper} />}
          </b2x.Div>
        )}
      </b2x.router.Link>
    </div>
  );
};
